import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages the frame option selector modal for cart upsells.
 * Allows user to choose between different frame options (wood, white, black, etc.)
 * when upgrading a cart item.
 *
 * @extends {DialogComponent}
 */
class FrameOptionSelectorModal extends DialogComponent {
  // Private instance variables
  /** @type {Object|null} */
  #currentConfig = null;

  /** @type {boolean} */
  #isProcessing = false;

  /** @type {boolean} */
  #cartDrawerWasOpen = false;

  /** @type {Map<HTMLButtonElement, Function>} */
  #buttonClickHandlers = new Map();

  /**
   * Opens the modal with variant options
   * @param {Object} config - Configuration object
   * @param {Array} config.variants - Array of variant objects with {id, sku, title, option}
   * @param {string} config.itemKey - Cart item key to be replaced
   * @param {number} config.originalVariantId - Original variant ID
   * @param {Object} config.itemProperties - Item properties to preserve
   * @returns {void}
   */
  open(config) {
    const { variants, itemKey, originalVariantId, itemProperties } = config;

    if (!variants || variants.length === 0) {
      console.error('No variants provided to frame option selector');
      return;
    }

    this.#currentConfig = {
      variants,
      itemKey,
      originalVariantId,
      itemProperties: itemProperties || {}
    };

    // Store cart drawer state
    const cartDrawer = document.querySelector('cart-drawer-component');
    this.#cartDrawerWasOpen = cartDrawer?.refs?.dialog?.open || false;

    // Render option buttons
    this.#renderOptions(variants);

    // Open the modal (with special handling for cart drawer)
    this.#openModalOverCartDrawer();
  }

  /**
   * Opens modal with special handling when cart drawer is open
   * Prevents double scroll-locking issues
   * @returns {void}
   */
  #openModalOverCartDrawer() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // If cart drawer is already open, don't manipulate body scroll
    // The cart drawer already has scroll lock in place
    if (this.#cartDrawerWasOpen) {
      dialog.showModal();
      this.dispatchEvent(new CustomEvent('dialog:open'));

      // Add event listeners
      setTimeout(() => {
        this.addEventListener('click', (e) => {
          if (e.target === dialog) this.close();
        });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
          }
        });
      });
    } else {
      // Cart drawer not open, use normal showDialog
      this.showDialog();
    }
  }

  /**
   * Closes the modal and resets state
   * @returns {void}
   */
  async close() {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // If cart drawer was open when we opened, just close the modal without manipulating body scroll
    if (this.#cartDrawerWasOpen) {
      // Add closing animation
      dialog.classList.add('dialog-closing');

      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 150));

      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.#resetState();
    } else {
      // Normal close with scroll restoration
      this.closeDialog();
      this.#resetState();
    }
  }

  /**
   * Renders frame option buttons
   * @param {Array} variants - Array of variant objects
   * @returns {void}
   */
  #renderOptions(variants) {
    const { optionsContainer } = this.refs;

    // Clean up old event listeners
    this.#cleanupEventListeners();

    // Clear container
    optionsContainer.innerHTML = '';

    variants.forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'frame-option-button';

      // Extract the last part of SKU as the option name (e.g., "wood", "white", "black")
      const optionName = variant.option || variant.sku.split('-').pop() || variant.title;
      button.textContent = optionName;

      button.dataset.variantId = String(variant.id);
      button.dataset.variantSku = variant.sku;

      if (!variant.available) {
        button.disabled = true;
        button.textContent += ' (Sold Out)';
      }

      // Create debounced handler
      const handler = this.#createDebouncedHandler(() => this.#handleOptionSelection(variant));

      // Store handler for cleanup
      this.#buttonClickHandlers.set(button, handler);

      button.addEventListener('click', handler);

      optionsContainer.appendChild(button);
    });
  }

  /**
   * Creates a debounced event handler
   * @param {Function} fn - The function to debounce
   * @returns {EventListener} The debounced function
   */
  #createDebouncedHandler(fn) {
    let timeoutId = null;

    return function debouncedHandler(...args) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        fn.apply(this, args);
        timeoutId = null;
      }, 300); // 300ms debounce
    };
  }

  /**
   * Handles frame option selection and performs the upsell swap
   * @param {Object} variant - The selected variant object
   * @returns {Promise<void>}
   */
  async #handleOptionSelection(variant) {
    // Prevent duplicate submissions
    if (this.#isProcessing) {
      return;
    }

    this.#isProcessing = true;

    try {
      // Disable all buttons during processing
      const buttons = this.refs.optionsContainer.querySelectorAll('button');
      buttons.forEach((btn) => (btn.disabled = true));

      // Perform the upsell swap using the cart upsell logic
      await this.#performUpsellSwap(variant.id);

      // Close modal after success
      this.close();
    } catch (error) {
      console.error('Error performing upsell:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upgrade item';
      this.#showError(errorMessage);

      // Re-enable buttons
      const buttons = this.refs.optionsContainer.querySelectorAll('button');
      buttons.forEach((btn) => (btn.disabled = false));
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Performs the upsell swap operation
   * Removes original item and adds the upgraded variant with same properties
   * @param {number} targetVariantId - The target variant ID
   * @returns {Promise<void>}
   */
  async #performUpsellSwap(targetVariantId) {
    const { itemKey, itemProperties } = this.#currentConfig;

    console.log('Performing upsell swap:', {
      itemKey,
      targetVariantId,
      itemProperties
    });

    // Step 1: Fetch current cart to get item details
    const cart = await fetch('/cart.js').then(res => res.json());
    const item = cart.items.find(i => i.key === itemKey);

    if (!item) {
      throw new Error('Item not found in cart');
    }

    // Extract all properties from the item
    const allProperties = item.properties || {};

    // Create new item input with target variant and copied properties
    const newItemInput = {
      id: targetVariantId,
      quantity: item.quantity,
      properties: { ...allProperties }
    };

    let newItems = [newItemInput];

    // Clone addon variants if present (TeeInBlue specific)
    if (allProperties._tib_addon_variant) {
      console.log('Cloning addon variants:', allProperties._tib_addon_variant);

      const variants = allProperties._tib_addon_variant
        .split('|')
        .map(v => v.split(':'))
        .map(([id, quantity]) => ({
          id: parseInt(id, 10),
          quantity: parseInt(quantity, 10),
          properties: {
            _tib_customization_id: allProperties._customization_id,
          }
        }));

      newItems = [...newItems, ...variants];
    }

    // Step 2: Remove original item from cart
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0
      })
    }).then(res => {
      if (!res.ok) throw new Error('Failed to remove original item');
      return res.json();
    });

    console.log('Original item removed');

    // Step 3: Add new items (variant + addons)
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: newItems })
    });

    if (!addResponse.ok) {
      throw new Error('Failed to add upgraded item');
    }

    const result = await addResponse.json();
    console.log('Upgraded item added:', result);

    // Step 4: Refresh cart UI
    await this.#refreshCart();
  }

  /**
   * Refreshes the cart UI using Shopify section rendering
   * @returns {Promise<void>}
   */
  async #refreshCart() {
    try {
      // Fetch updated cart data and dispatch event
      const cart = await fetch('/cart.js').then(res => res.json());

      document.dispatchEvent(new CustomEvent('cart:update', {
        detail: { cart }
      }));

      // Refresh cart drawer using section rendering (preserves component state)
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh();
      }
    } catch (err) {
      console.error('Failed to refresh cart:', err);
    }
  }

  /**
   * Shows error message
   * @param {string} message - Error message
   * @returns {void}
   */
  #showError(message) {
    const { errorState, errorMessage } = this.refs;

    if (errorMessage) {
      errorMessage.textContent = message;
    }

    if (errorState) {
      errorState.style.display = 'block';
    }
  }

  /**
   * Cleans up event listeners from previous renders
   * @returns {void}
   */
  #cleanupEventListeners() {
    this.#buttonClickHandlers.forEach((handler, button) => {
      button.removeEventListener('click', handler);
    });
    this.#buttonClickHandlers.clear();
  }

  /**
   * Resets modal state
   * @returns {void}
   */
  #resetState() {
    this.#currentConfig = null;
    this.#isProcessing = false;
    this.#cartDrawerWasOpen = false;

    // Clean up event listeners
    this.#cleanupEventListeners();

    if (this.refs.optionsContainer) {
      this.refs.optionsContainer.innerHTML = '';
    }

    if (this.refs.errorState) {
      this.refs.errorState.style.display = 'none';
    }
  }

  /**
   * Lifecycle method called when element is removed from DOM
   * @returns {void}
   */
  disconnectedCallback() {
    this.#cleanupEventListeners();
    this.#resetState();

    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }
}

if (!customElements.get('frame-option-selector-modal')) {
  customElements.define('frame-option-selector-modal', FrameOptionSelectorModal);
}
